// Après « Copier l'e-mail » ou « Ouvrir LinkedIn » sur une fiche suivie (conception, § 5) : la barre demande si le
// message est parti. Rien n'est noté sans « Oui » : copier une adresse ne prouve pas l'envoi.
import { useTranslate } from "ra-core";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";

import type { Deal } from "../types";
import {
  FollowUpOffer,
  type JournalContact,
  useLogActivity,
} from "./DealJournal";
import { type ProposedAction, proposeFollowUp, today } from "./nextAction";

const SHOWN_MS = 20_000;

export const SentPrompt = ({
  deal,
  contact,
  kind,
  onClose,
}: {
  deal: Deal;
  contact: JournalContact;
  kind: "email" | "linkedin";
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const { log } = useLogActivity();
  const [proposal, setProposal] = useState<ProposedAction | null>(null);
  const [busy, setBusy] = useState(false);
  // Le parent passe une flèche neuve à chaque rendu : on la garde en réserve pour ne pas relancer le délai.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (proposal) return;
    const timer = setTimeout(() => closeRef.current(), SHOWN_MS);
    return () => clearTimeout(timer);
  }, [proposal, contact.id, kind]);

  return (
    <div className="fixed inset-x-4 bottom-20 z-50 mx-auto max-w-lg rounded-lg border bg-background p-3 shadow-lg md:bottom-6">
      {proposal ? (
        <FollowUpOffer deal={deal} proposal={proposal} onClose={onClose} />
      ) : (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="flex-1">
            {translate("resources.projects.contacts.sent_prompt", {
              name: contact.name,
            })}
          </span>
          <Button
            size="sm"
            disabled={busy}
            onClick={async () => {
              if (busy) return;
              setBusy(true);
              try {
                const activity = await log({
                  deal_id: deal.id,
                  contact_id: contact.id,
                  kind,
                  direction: "out",
                });
                if (!activity) return;
                const next = proposeFollowUp(deal, contact, today());
                if (next) setProposal(next);
                else onClose();
              } finally {
                setBusy(false);
              }
            }}
          >
            {translate("resources.projects.contacts.sent_yes")}
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={onClose}>
            {translate("resources.projects.contacts.sent_later")}
          </Button>
        </div>
      )}
    </div>
  );
};
